import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from '~/lib/api-client';
import { approvalKeys } from '~/lib/query-keys';

/**
 * FRONT-2307: where a late approval's tool call stands, polled only after the
 * approve route answered `executed: "running"` (a long sandbox command
 * outlives the route's ~25s wait). The card needs the settled status — and
 * the sentence it settled with — to show how it ended
 * (`late-approval-outcome.tsx`). Display only: the server starts Eddy's
 * continuation itself (FRONT-3036).
 *
 * Polled rather than pushed: the SSE contract (use-sse.ts) is "events
 * invalidate, never carry business state".
 */
export interface ApprovalExecution {
  id: string;
  executionStatus: 'claimed' | 'succeeded' | 'failed' | 'refused' | null;
  /** The user-facing line the run settled with; null while it is running. */
  message?: string | null;
  /**
   * When the server will have settled this run no matter what: its own
   * stuck-claim threshold (`LATE_RUN_MAX_MS`, sized to the longest sandbox
   * command) plus one reconciler heartbeat. The watcher waits exactly this
   * long, never a number of its own (Dome, PR #1624: a 25-minute build must
   * not read as lost).
   */
  settleBy?: string | null;
}

const POLL_MS = 3_000;
/** After the first few minutes a long build is polled less eagerly. */
const SLOW_POLL_MS = 15_000;
const FAST_WINDOW_MS = 5 * 60_000;
/** Only when the server sent no `settleBy` (a row with no claim time). */
export const FALLBACK_MAX_POLL_MS = 70 * 60_000;
/** Slack after `settleBy` for the reconciler's own write to land. */
const SETTLE_GRACE_MS = 60_000;

/** When the watcher stops waiting and reports the run as lost. */
export function watchDeadline(data: ApprovalExecution | undefined, startedAt: number): number {
  const settleBy = data?.settleBy ? Date.parse(data.settleBy) : NaN;
  return Number.isNaN(settleBy) ? startedAt + FALLBACK_MAX_POLL_MS : settleBy + SETTLE_GRACE_MS;
}

/**
 * Pure for its own test: keep polling while the run is unsettled and the
 * server has not yet passed its own settle deadline. A 404 stops at once —
 * the row is gone or not ours, so asking again changes nothing. Any other
 * failure (a dropped fetch, a 5xx, a throttled background tab) is
 * transient: the query's own retries run first, and the poll carries on
 * after them rather than giving up on the hand-back for good.
 */
export function approvalExecutionRefetchInterval(
  data: ApprovalExecution | undefined,
  startedAt: number,
  now: number = Date.now(),
  error: unknown = null,
): number | false {
  if (now > watchDeadline(data, startedAt)) return false;
  if (error instanceof ApiError && error.status === 404) return false;
  const s = data?.executionStatus;
  if (s === 'succeeded' || s === 'failed' || s === 'refused') return false;
  return now - startedAt < FAST_WINDOW_MS ? POLL_MS : SLOW_POLL_MS;
}

export function useApprovalExecution(approvalId: string, startedAt: number) {
  return useQuery({
    queryKey: approvalKeys.execution(approvalId),
    queryFn: () => api.get<ApprovalExecution>(`/api/approvals/${approvalId}/execution`),
    refetchInterval: (q) => approvalExecutionRefetchInterval(q.state.data, startedAt, Date.now(), q.state.error),
    // Keep polling through a transient failure; the interval above is what
    // decides when to stop.
    refetchIntervalInBackground: true,
  });
}
